import { useEffect } from 'react'
import DatePicker, { DatePickerProps, registerLocale } from 'react-datepicker'
import classNames from 'classnames'
import cs from 'date-fns/locale/cs'
import en from 'date-fns/locale/en-US'

import { useVisibleState } from '@foxino/components-common'
import { ChevronDown } from '@foxino/icons-react'

import resolveTailwindClasses from '@app/utils/resolveTailwindClasses'

import 'react-datepicker/dist/react-datepicker.css'

registerLocale('cs-CZ', cs)
registerLocale('en-US', en)

const errorClasses = 'bg-transparent border-error-500 placeholder:text-neutral-500 focus-within:border-error-500'
const validClasses =
  'bg-transparent border-primary-600 placeholder:text-neutral-500 focus-within:border-primary-600 cursor-pointer'
const disabledClasses = 'bg-primary-1000 text-primary-600 border-primary-700 placeholder:text-primary-600'

type Props = DatePickerProps & {
  datePickerClassName?: string
  inputClassName?: string
  labelClassName?: string
  label?: string
  hasError?: boolean
  errorText?: string
  description?: string
}

/**
 * @group Components
 * @category Props
 */
export const DatePickerInput = ({
  className,
  showIcon,
  datePickerClassName,
  inputClassName,
  labelClassName,
  disabled,
  hasError,
  errorText,
  label,
  description,
  onCalendarClose,
  ...restProps
}: Props) => {
  const [calendarVisible, showCalendar, hideCalendar] = useVisibleState()

  useEffect(() => {
    if (!calendarVisible && onCalendarClose) {
      onCalendarClose()
    }
  }, [calendarVisible])

  const handleOnShowClick = () => {
    if (disabled || calendarVisible) return
    showCalendar()
  }

  return (
    <div className={classNames('flex flex-col gap-2', className)}>
      {label ? (
        <label
          htmlFor={restProps.id || restProps.name}
          className={classNames('text-sm text-primary-200', labelClassName)}
        >
          {label}
        </label>
      ) : null}

      <div
        onClick={handleOnShowClick}
        className={resolveTailwindClasses(
          'bg-primary-transparent flex flex-row items-center justify-between rounded-lg border border-primary-600 py-2 pr-2',
          disabled ? disabledClasses : hasError || errorText ? errorClasses : validClasses,
          showIcon ? 'pl-0' : 'pl-2',
          inputClassName
        )}
      >
        <DatePicker
          className={resolveTailwindClasses('w-fit', datePickerClassName)}
          open={calendarVisible}
          onInputClick={handleOnShowClick}
          onClickOutside={hideCalendar}
          disabled={disabled}
          showIcon={showIcon}
          {...restProps}
        />
        <ChevronDown
          className={classNames('size-5 text-neutral-400', disabled && 'text-primary-600')}
          onClick={handleOnShowClick}
        />
      </div>
      {errorText ? (
        <div className="text-sm text-error-500">{errorText}</div>
      ) : description ? (
        <div className="text-sm text-neutral-500">{description}</div>
      ) : null}
    </div>
  )
}
